import { Mode } from '@/contexts/ModeContext';
import { weatherService, type WeatherData } from './weatherService';

export interface Message {
  id: string;
  content: string;
  type: 'assistant' | 'user';
  timestamp: string;
  status?: 'sending' | 'sent' | 'error';
}

interface ChatResponse {
  message: string;
  suggestions?: string[];
}

class ChatService {
  private apiKey: string;
  private apiUrl: string;
  private weatherData: WeatherData | null = null;

  constructor() {
    this.apiKey = import.meta.env.VITE_HUGGINGFACE_API_KEY;
    this.apiUrl = 'https://api-inference.huggingface.co/models/gpt2';
  }

  private async fetchWithTimeout(url: string, options: RequestInit, timeout = 10000) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  }

  private async queryModel(input: string): Promise<string> {
    try {
      const response = await this.fetchWithTimeout(
        this.apiUrl,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            inputs: input,
            parameters: {
              max_length: 100,
              temperature: 0.7,
              top_p: 0.9,
              do_sample: true
            }
          }),
        },
        10000
      );

      if (!response.ok) {
        throw new Error('Failed to get response from AI model');
      }

      const data = await response.json();
      return data[0].generated_text;
    } catch (error) {
      console.error('Error querying AI model:', error);
      throw error;
    }
  }

  private async updateWeatherData() {
    try {
      // Use default coordinates if geolocation is not available
      const defaultCoords = { lat: 51.5074, lon: -0.1278 };
      const result = await weatherService.getForecast(defaultCoords.lat, defaultCoords.lon);
      
      if ('message' in result) {
        console.error('Weather error:', result.message);
        return;
      }
      
      this.weatherData = result;
    } catch (error) {
      console.error('Error fetching weather data:', error);
    }
  }

  async getResponse(message: string, mode: Mode): Promise<ChatResponse> {
    try {
      // Update weather data if needed
      if (!this.weatherData) {
        await this.updateWeatherData();
      }

      // For now, use predefined responses based on keywords and context
      const lowerMessage = message.toLowerCase();
      
      if (mode === 'household') {
        if (lowerMessage.includes('usage')) {
          return {
            message: "Based on your recent data, your household water usage is 15% lower than last month. You're doing great! Would you like to see a detailed breakdown?",
            suggestions: [
              "Show me my daily usage",
              "Compare with neighborhood average",
              "How can I save more?"
            ]
          };
        } else if (lowerMessage.includes('leak')) {
          return {
            message: "I've analyzed your water usage patterns and detected unusual activity between 2-4 AM, which might indicate a leak. Would you like me to help you run a leak detection test?",
            suggestions: [
              "Run leak detection test",
              "Show suspicious activity",
              "Schedule plumber visit"
            ]
          };
        } else if (lowerMessage.includes('save')) {
          const weatherAdvice = this.weatherData ? 
            `\n\nBased on the weather forecast (${this.weatherData.temperature}°C, ${this.weatherData.description}), you might want to adjust your garden watering schedule.` : '';

          return {
            message: `Here are some personalized water-saving tips for your household: 
1) Install low-flow showerheads to save up to 2,700 gallons annually
2) Fix that dripping faucet in the kitchen - it wastes about 3,000 gallons per year
3) Consider a smart irrigation system for your garden${weatherAdvice}`,
            suggestions: [
              "More water-saving tips",
              "Calculate potential savings",
              "Shop water-efficient products"
            ]
          };
        } else if (lowerMessage.includes('weather')) {
          if (this.weatherData) {
            const advice = weatherService.getIrrigationAdvice(this.weatherData);
            return {
              message: `Current weather: ${this.weatherData.temperature}°C, ${this.weatherData.description}. ${advice}`,
              suggestions: [
                "Show weekly forecast",
                "Adjust watering schedule",
                "Get efficiency tips"
              ]
            };
          }
        }
      } else {
        if (lowerMessage.includes('usage') || lowerMessage.includes('irrigation')) {
          return {
            message: "Your farm's irrigation efficiency has improved by 20% this season. Field sector B shows slightly higher water usage than optimal. Would you like to see the detailed irrigation analysis?",
            suggestions: [
              "Show irrigation analysis",
              "Optimize irrigation schedule",
              "View water distribution map"
            ]
          };
        } else if (lowerMessage.includes('weather')) {
          if (this.weatherData) {
            const advice = weatherService.getIrrigationAdvice(this.weatherData);
            const forecast = this.weatherData.forecast[0];
            return {
              message: `Current conditions: ${this.weatherData.temperature}°C, ${this.weatherData.description}. 
Tomorrow's forecast: ${forecast.temperature}°C, ${forecast.description}.

${advice}

Would you like me to suggest an optimized irrigation schedule based on this forecast?`,
              suggestions: [
                "Show detailed forecast",
                "Adjust irrigation schedule",
                "Calculate water savings"
              ]
            };
          }
        } else if (lowerMessage.includes('efficiency')) {
          return {
            message: "Your current irrigation efficiency score is 86/100. Here are some suggestions to improve: 1) Upgrade to drip irrigation in sector C, 2) Adjust sprinkler angles in sector B, 3) Implement soil moisture sensors.",
            suggestions: [
              "View efficiency details",
              "Calculate upgrade costs",
              "Schedule maintenance"
            ]
          };
        }
      }

      // Default response with suggestions
      return {
        message: "I'm here to help you manage your water usage efficiently. What would you like to know?",
        suggestions: [
          mode === 'household' 
            ? "How's my water usage this month?"
            : "Show irrigation efficiency",
          "Check for potential leaks",
          "Get water-saving tips"
        ]
      };
    } catch (error) {
      console.error('Error getting chat response:', error);
      throw error;
    }
  }

  getSuggestions(mode: Mode): string[] {
    return mode === 'household'
      ? [
          "How can I reduce my water bill?",
          "Do I have any leaks?",
          "Show my water usage trends",
          "Compare with neighborhood average"
        ]
      : [
          "How's my irrigation efficiency?",
          "Optimize irrigation schedule",
          "Show water distribution map",
          "Get weather-based recommendations"
        ];
  }

  getWelcomeMessage(mode: Mode): string {
    return mode === 'household'
      ? "Hi! I'm your personal water conservation assistant. I can help you track usage, detect leaks, and save water. What would you like to know?"
      : "Welcome! I'm your agricultural water management assistant. I can help optimize irrigation, track efficiency, and provide weather-based recommendations. How can I assist you?";
  }
}

export const chatService = new ChatService(); 